import { useRef } from 'react';

const MIN_SWIPE_DISTANCE = 50;

function isMultiTouchEvent(event: React.TouchEvent | TouchEvent): boolean {
  return event.touches.length > 1;
}

function getClientX(event: React.TouchEvent | TouchEvent): number {
  return event.changedTouches[0].clientX;
}

const getSwipeDirection = (
  startX: number,
  endX: number
): 'left' | 'right' | null => {
  const deltaX = startX - endX;
  // Too short to be a swipe
  if (Math.abs(deltaX) < MIN_SWIPE_DISTANCE) {
    return null;
  }
  if (deltaX < 0) {
    return 'right';
  }
  return 'left';
};

const shouldIgnoreTouch = (event: React.TouchEvent | TouchEvent): boolean => {
  if (event.shiftKey || isMultiTouchEvent(event)) {
    return true;
  }
  return false;
};

export const useCarouselSwipe = (
  onSwipe: (direction: 'left' | 'right') => void
) => {
  const touchStartXRef = useRef<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (shouldIgnoreTouch(e)) {
      return;
    }
    touchStartXRef.current = getClientX(e);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null) {
      return;
    }
    if (shouldIgnoreTouch(e)) {
      touchStartXRef.current = null;
      return;
    }
    // Prevent default to avoid scrolling while swiping horizontally
    const currentX = getClientX(e);
    const deltaX = Math.abs(currentX - touchStartXRef.current);
    // Only prevent default if we've moved horizontally enough
    if (deltaX > 10) {
      e.preventDefault();
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null) {
      return;
    }
    if (shouldIgnoreTouch(e)) {
      touchStartXRef.current = null;
      return;
    }
    const endX = getClientX(e);
    const dir = getSwipeDirection(touchStartXRef.current, endX);
    touchStartXRef.current = null;
    if (dir) {
      // Prevent browser navigation gestures when a swipe is detected
      e.preventDefault();
      onSwipe(dir);
    }
  };

  return {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  };
};
